/* Telegram Mini App «Аналитика Ainanan» — мобильная панель для руководства.
   Открывается внутри бота ainananmanagement_bot (web_app). RU/EN.
   Авторизация: подписанный initData (заголовок X-Telegram-Init-Data). Read-only. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/analytics-app`;

const T = {
  ru: {
    title: 'Аналитика Ainanan', loading: 'Загрузка…',
    notTelegram: 'Откройте через Telegram',
    notTelegramHint: 'Эта панель работает внутри бота аналитики Ainanan. Откройте её кнопкой «📊 Открыть аналитику» в чате с ботом.',
    notBound: 'Нет доступа', notBoundHint: 'Ваш Telegram не привязан. Запросите персональную ссылку у администратора.',
    period: 'Период', d7: '7 дней', d30: '30 дней', d90: '90 дней', custom: 'Свой',
    from: 'С', to: 'По', apply: 'Применить',
    tabs: { overview: 'Обзор', revenue: 'Выручка', top: 'Топ', payments: 'Платежи', churn: 'Отток', tare: 'Тара', delivery: 'Доставка' },
    revenue_: 'Выручка', orders: 'Заказы', aov: 'Средний чек', customers: 'Клиенты', cancelled: 'Отмены',
    revTrend: 'Динамика выручки', topProducts: 'Топ товары', topClients: 'Топ клиенты', byCategory: 'По категориям',
    conversion: 'Конверсия', paid: 'Оплачено', failed: 'Ошибки', processing: 'В процессе', lost: 'Потеряно', intents: 'Платежей',
    atRisk: 'Под наблюдением', highRisk: 'Высокий риск', lostC: 'Потеряно', revAtRisk: 'Выручка в зоне риска',
    forgottenTare: 'Забытая тара', balance: 'Остаток', days: 'дн.',
    routes: 'Маршрутов', deliveries: 'Доставок', totalKm: 'Всего км', kmRoute: 'Км/маршрут', kmDelivery: 'Км/дост.',
    byDriver: 'По водителям', driver: 'Водитель', problems: 'Проблем', noData: 'Нет данных',
    units: 'шт', orders_: 'зак.',
  },
  en: {
    title: 'Ainanan Analytics', loading: 'Loading…',
    notTelegram: 'Open via Telegram',
    notTelegramHint: 'This dashboard runs inside the Ainanan analytics bot. Open it with the “📊 Open analytics” button in the bot chat.',
    notBound: 'No access', notBoundHint: 'Your Telegram is not linked. Ask an administrator for a personal link.',
    period: 'Period', d7: '7 days', d30: '30 days', d90: '90 days', custom: 'Custom',
    from: 'From', to: 'To', apply: 'Apply',
    tabs: { overview: 'Overview', revenue: 'Revenue', top: 'Top', payments: 'Payments', churn: 'Churn', tare: 'Containers', delivery: 'Delivery' },
    revenue_: 'Revenue', orders: 'Orders', aov: 'Avg order', customers: 'Customers', cancelled: 'Cancelled',
    revTrend: 'Revenue trend', topProducts: 'Top products', topClients: 'Top clients', byCategory: 'By category',
    conversion: 'Conversion', paid: 'Paid', failed: 'Failed', processing: 'Processing', lost: 'Lost', intents: 'Payments',
    atRisk: 'At risk', highRisk: 'High risk', lostC: 'Lost', revAtRisk: 'Revenue at risk',
    forgottenTare: 'Forgotten containers', balance: 'Balance', days: 'd',
    routes: 'Routes', deliveries: 'Deliveries', totalKm: 'Total km', kmRoute: 'Km/route', kmDelivery: 'Km/delivery',
    byDriver: 'By driver', driver: 'Driver', problems: 'Problems', noData: 'No data',
    units: 'pcs', orders_: 'ord.',
  },
};

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const INITIAL_HASH = typeof window !== 'undefined' ? window.location.hash : '';
function hashInitData() {
  for (const h of [INITIAL_HASH, typeof window !== 'undefined' ? window.location.hash : '']) {
    try {
      const v = new URLSearchParams((h || '').replace(/^#/, '')).get('tgWebAppData');
      if (v) return v;
    } catch (e) { /* noop */ }
  }
  return '';
}

const nf = new Intl.NumberFormat('ru-RU');
const money = (n) => nf.format(Math.round(n || 0)) + ' ₸';
const compact = (n) => {
  n = Math.round(n || 0);
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1).replace('.0', '') + 'M ₸';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(0) + 'K ₸';
  return n + ' ₸';
};

function Delta({ v }) {
  if (v === null || v === undefined) return null;
  const up = v >= 0;
  return (
    <span className={`text-xs font-semibold ${up ? 'text-emerald-400' : 'text-red-400'}`}>
      {up ? '▲' : '▼'} {Math.abs(v).toFixed(1)}%
    </span>
  );
}

function Kpi({ label, value, delta, accent }) {
  return (
    <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4" data-testid={`kpi-${label}`}>
      <div className="text-xs text-zinc-400">{label}</div>
      <div className={`text-2xl font-extrabold mt-1 ${accent || 'text-zinc-50'}`}>{value}</div>
      {delta !== undefined && <div className="mt-1"><Delta v={delta} /></div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
      {title && <div className="text-sm font-semibold text-zinc-200 mb-3">{title}</div>}
      {children}
    </div>
  );
}

export default function AnalyticsApp() {
  const [lang, setLang] = useState(localStorage.getItem('analytics_lang') || 'ru');
  const t = T[lang];
  const [initData, setInitData] = useState('');
  const [phase, setPhase] = useState('loading'); // loading|notg|notbound|ready
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('overview');
  const [period, setPeriod] = useState('30');
  const [cFrom, setCFrom] = useState('');
  const [cTo, setCTo] = useState('');
  const [cache, setCache] = useState({});
  const [busy, setBusy] = useState(false);

  const switchLang = (l) => { setLang(l); localStorage.setItem('analytics_lang', l); };

  // --- Telegram boot ---
  useEffect(() => {
    const boot = () => {
      const tg = window.Telegram?.WebApp;
      const data = tg?.initData || hashInitData();
      if (tg) { try { tg.expand(); tg.ready(); tg.setHeaderColor?.('#09090b'); tg.setBackgroundColor?.('#09090b'); } catch (e) { /* noop */ } }
      if (!data) { setPhase('notg'); return; }
      setInitData(data);
    };
    if (window.Telegram?.WebApp) { boot(); return; }
    const s = document.createElement('script');
    s.src = 'https://telegram.org/js/telegram-web-app.js';
    s.onload = boot;
    s.onerror = () => setPhase('notg');
    document.head.appendChild(s);
  }, []);

  const api = useCallback(async (path, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const res = await fetch(`${API}${path}${qs ? '?' + qs : ''}`, {
      headers: { 'X-Telegram-Init-Data': initData },
    });
    if (!res.ok) { const e = new Error('err'); e.status = res.status; throw e; }
    return res.json();
  }, [initData]);

  // auth check
  useEffect(() => {
    if (!initData) return;
    (async () => {
      try { const m = await api('/me'); setMe(m); setPhase('ready'); }
      catch (e) { setPhase(e.status === 403 ? 'notbound' : 'notg'); }
    })();
  }, [initData, api]);

  const periodParams = useMemo(() => {
    if (period === 'custom' && cFrom && cTo) return { start_date: cFrom, end_date: cTo };
    return { days: period };
  }, [period, cFrom, cTo]);

  const cacheKey = (p) => `${tab}:${p}:${JSON.stringify(periodParams)}`;

  const fetchFor = useCallback(async () => {
    if (phase !== 'ready') return;
    const map = {
      overview: ['/overview'],
      revenue: ['/revenue-trend'],
      top: ['/top-products', '/top-clients', '/category-breakdown'],
      payments: ['/funnel'],
      churn: ['/churn'],
      tare: ['/aging'],
      delivery: ['/delivery-ops'],
    };
    const paths = map[tab];
    const needed = paths.filter((p) => cache[cacheKey(p)] === undefined);
    if (needed.length === 0) return;
    setBusy(true);
    try {
      const results = await Promise.all(needed.map((p) => {
        const params = (p === '/churn' || p === '/aging') ? {} : periodParams;
        const extra = (p === '/top-products' || p === '/top-clients') ? { limit: 8 } : {};
        return api(p, { ...params, ...extra }).then((d) => [p, d]).catch(() => [p, null]);
      }));
      setCache((c) => { const nc = { ...c }; results.forEach(([p, d]) => { nc[cacheKey(p)] = d; }); return nc; });
    } finally { setBusy(false); }
  }, [phase, tab, periodParams, api, cache]);

  useEffect(() => { fetchFor(); /* eslint-disable-next-line */ }, [phase, tab, period, cFrom, cTo]);

  const get = (p) => cache[cacheKey(p)];

  // ---------- gates ----------
  if (phase === 'loading') {
    return <Center><div className="text-zinc-400">{t.loading}</div></Center>;
  }
  if (phase === 'notg' || phase === 'notbound') {
    const nb = phase === 'notbound';
    return (
      <Center>
        <div className="text-center max-w-xs">
          <div className="text-5xl mb-4">{nb ? '🔒' : '📊'}</div>
          <div className="text-xl font-bold text-zinc-100 mb-2">{nb ? t.notBound : t.notTelegram}</div>
          <div className="text-sm text-zinc-400">{nb ? t.notBoundHint : t.notTelegramHint}</div>
        </div>
      </Center>
    );
  }

  // ---------- main ----------
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 pb-10" data-testid="analytics-app">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-zinc-950/90 backdrop-blur border-b border-zinc-800">
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <div>
            <div className="text-lg font-extrabold tracking-tight">{t.title}</div>
            {me?.name && <div className="text-xs text-zinc-500">{me.name}</div>}
          </div>
          <div className="flex rounded-full bg-zinc-900 border border-zinc-800 p-0.5 text-xs">
            {['ru', 'en'].map((l) => (
              <button key={l} onClick={() => switchLang(l)} data-testid={`lang-${l}`}
                className={`px-3 py-1 rounded-full font-semibold ${lang === l ? 'bg-emerald-600 text-white' : 'text-zinc-400'}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Period */}
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
          {[['7', t.d7], ['30', t.d30], ['90', t.d90], ['custom', t.custom]].map(([v, lbl]) => (
            <button key={v} onClick={() => setPeriod(v)} data-testid={`period-${v}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${period === v ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-zinc-900 text-zinc-300 border-zinc-800'}`}>
              {lbl}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="px-4 pb-3 flex items-center gap-2 text-xs">
            <input type="date" value={cFrom} onChange={(e) => setCFrom(e.target.value)} data-testid="custom-from"
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-200" />
            <span className="text-zinc-500">—</span>
            <input type="date" value={cTo} onChange={(e) => setCTo(e.target.value)} data-testid="custom-to"
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-1.5 text-zinc-200" />
          </div>
        )}

        {/* Tabs */}
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
          {Object.entries(t.tabs).map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold ${tab === k ? 'bg-zinc-100 text-zinc-900' : 'bg-zinc-900 text-zinc-400'}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {busy && <div className="text-center text-xs text-zinc-500">{t.loading}</div>}

        {tab === 'overview' && <Overview t={t} d={get('/overview')} />}
        {tab === 'revenue' && <Revenue t={t} d={get('/revenue-trend')} />}
        {tab === 'top' && <Top t={t} products={get('/top-products')} clients={get('/top-clients')} cats={get('/category-breakdown')} />}
        {tab === 'payments' && <Payments t={t} d={get('/funnel')} />}
        {tab === 'churn' && <Churn t={t} d={get('/churn')} />}
        {tab === 'tare' && <Tare t={t} d={get('/aging')} />}
        {tab === 'delivery' && <Delivery t={t} d={get('/delivery-ops')} />}
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">{children}</div>;
}

/* ---------------- Sections ---------------- */

function Overview({ t, d }) {
  if (!d) return <Skeleton />;
  return (
    <div className="grid grid-cols-2 gap-3">
      <Kpi label={t.revenue_} value={compact(d.total_revenue)} delta={d.revenue_change_percent} accent="text-emerald-400" />
      <Kpi label={t.orders} value={nf.format(d.total_orders)} delta={d.orders_change_percent} />
      <Kpi label={t.aov} value={compact(d.avg_order_value)} delta={d.avg_order_change_percent} />
      <Kpi label={t.customers} value={nf.format(d.unique_customers)} delta={d.customers_change_percent} />
      <Kpi label={t.cancelled} value={`${d.cancelled_orders} · ${d.cancellation_rate}%`} accent="text-amber-400" />
    </div>
  );
}

function Revenue({ t, d }) {
  if (!d) return <Skeleton />;
  const data = (d || []).map((x) => ({ date: x.date?.slice(5), revenue: x.revenue }));
  return (
    <Section title={t.revTrend}>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => compact(v).replace(' ₸', '')} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 12, color: '#fafafa' }}
              formatter={(v) => money(v)} />
            <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#rev)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Section>
  );
}

function Top({ t, products, clients, cats }) {
  return (
    <div className="space-y-4">
      {cats && cats.length > 0 && (
        <Section title={t.byCategory}>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={cats.slice(0, 8)} dataKey={cats[0]?.revenue !== undefined ? 'revenue' : 'value'}
                  nameKey={cats[0]?.category !== undefined ? 'category' : 'name'} cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                  {cats.slice(0, 8).map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 12, color: '#fafafa' }}
                  formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Section>
      )}
      <Section title={t.topProducts}>
        <RankList items={(products || []).map((p) => ({ name: p.name || p.product_name, value: money(p.revenue), sub: `${nf.format(p.quantity || p.units_sold || 0)} ${t.units}` }))} />
      </Section>
      <Section title={t.topClients}>
        <RankList items={(clients || []).map((c) => ({ name: c.name || c.client_name, value: money(c.revenue), sub: `${nf.format(c.orders || c.order_count || 0)} ${t.orders_}` }))} />
      </Section>
    </div>
  );
}

function RankList({ items }) {
  if (!items || items.length === 0) return <Empty />;
  return (
    <div className="divide-y divide-zinc-800">
      {items.map((it, i) => (
        <div key={i} className="flex items-center justify-between py-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`w-6 h-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${i < 3 ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>{i + 1}</span>
            <span className="truncate text-sm text-zinc-200">{it.name}</span>
          </div>
          <div className="text-right shrink-0 ml-2">
            <div className="text-sm font-semibold text-zinc-100">{it.value}</div>
            <div className="text-xs text-zinc-500">{it.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Payments({ t, d }) {
  if (!d) return <Skeleton />;
  const s = d.summary || {};
  const statusData = [
    { name: t.paid, value: s.paid_count, fill: '#10b981' },
    { name: t.processing, value: s.processing_count, fill: '#f59e0b' },
    { name: t.failed, value: s.failed_count, fill: '#ef4444' },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Kpi label={t.conversion} value={`${s.conversion_rate ?? 0}%`} accent="text-emerald-400" />
        <Kpi label={t.intents} value={nf.format(s.total_intents)} />
        <Kpi label={t.paid} value={compact(s.paid_amount)} accent="text-emerald-400" />
        <Kpi label={t.lost} value={compact(s.lost_amount)} accent="text-red-400" />
      </div>
      <Section title={`${t.paid} · ${t.processing} · ${t.failed}`}>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={statusData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 11 }} />
              <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 12, color: '#fafafa' }} cursor={{ fill: '#27272a55' }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>
    </div>
  );
}

function Churn({ t, d }) {
  if (!d) return <Skeleton />;
  const s = d.summary || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Kpi label={t.atRisk} value={nf.format(s.at_risk)} accent="text-amber-400" />
        <Kpi label={t.highRisk} value={nf.format(s.high_risk)} accent="text-red-400" />
        <Kpi label={t.lostC} value={nf.format(s.lost)} accent="text-zinc-400" />
        <Kpi label={t.revAtRisk} value={compact(s.revenue_at_risk)} accent="text-red-400" />
      </div>
      <Section>
        {(d.clients || []).length === 0 ? <Empty /> : (
          <div className="divide-y divide-zinc-800">
            {(d.clients || []).slice(0, 20).map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500">{c.segment} · {c.days_since} {t.days}</div>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <div className="text-sm font-semibold">{money(c.revenue)}</div>
                  <RiskBadge risk={c.risk} t={t} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function RiskBadge({ risk, t }) {
  const m = { at_risk: ['bg-amber-500/15 text-amber-400', t.atRisk], high_risk: ['bg-red-500/15 text-red-400', t.highRisk], lost: ['bg-zinc-700/40 text-zinc-400', t.lostC] };
  const [cls, lbl] = m[risk] || ['', risk];
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cls}`}>{lbl}</span>;
}

function Tare({ t, d }) {
  if (!d) return <Skeleton />;
  const s = d.summary || {};
  const list = d.aging_list || d.forgotten || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Kpi label={t.balance} value={nf.format(s.total_outstanding ?? s.total_balance ?? 0)} accent="text-emerald-400" />
        <Kpi label={t.forgottenTare} value={nf.format(list.length)} accent="text-amber-400" />
      </div>
      <Section title={t.forgottenTare}>
        {list.length === 0 ? <Empty /> : (
          <div className="divide-y divide-zinc-800">
            {list.slice(0, 20).map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{c.name || c.client_name}</div>
                  <div className="text-xs text-zinc-500">{c.age_days ?? c.days ?? '—'} {t.days}</div>
                </div>
                <div className="text-sm font-semibold shrink-0 ml-2">{nf.format(c.balance ?? c.outstanding ?? 0)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Delivery({ t, d }) {
  if (!d) return <Skeleton />;
  const s = d.summary || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Kpi label={t.routes} value={nf.format(s.routes)} />
        <Kpi label={t.deliveries} value={nf.format(s.deliveries)} />
        <Kpi label={t.totalKm} value={`${nf.format(s.total_km)} км`} accent="text-emerald-400" />
        <Kpi label={t.kmDelivery} value={`${s.avg_km_delivery ?? 0}`} />
      </div>
      <Section title={t.byDriver}>
        {(d.drivers || []).length === 0 ? <Empty /> : (
          <div className="divide-y divide-zinc-800">
            {(d.drivers || []).map((dr, i) => (
              <div key={i} className="flex items-center justify-between py-2.5">
                <div className="text-sm text-zinc-200">{dr.driver}</div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-zinc-400">{dr.deliveries} {t.orders_}</span>
                  <span className="text-zinc-300">{dr.km} км</span>
                  <span className={dr.problems ? 'text-red-400 font-semibold' : 'text-zinc-600'}>{dr.problems}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 animate-pulse">
      {[0, 1, 2, 3].map((i) => <div key={i} className="h-24 rounded-2xl bg-zinc-900 border border-zinc-800" />)}
    </div>
  );
}

function Empty() {
  return <div className="py-6 text-center text-sm text-zinc-500">—</div>;
}
